import React, { useCallback, useMemo } from 'react';
import {
  SelectSingle,
  Props as SelectSingleProps,
} from '../../../components/Selects/SelectSingle';
import { Skill } from './types';
import { OptionBase } from '../../../../../components/Select/types';
import { RenderOptionParams } from '../../../components/Selects/SelectSingleBase';
import { SkillOptionLabel } from './SkillOptionLabel';
import { OptionLabel } from '../../../components/Selects/OptionLabel';
import styled from '@emotion/styled';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';

type Props = Omit<
  SelectSingleProps,
  'value' | 'options' | 'onChange' | 'renderOption'
> & {
  value: string | null;
  allSkills: Skill[];
  onChange: (value: string | null) => void;
};

export const SkillSingleSelect: React.FC<React.PropsWithChildren<Props>> = ({
  allSkills,
  placeholder,
  ...props
}) => {
  const t = useNamespacedTranslation('skills.addEmployeeSkill');

  // Search must match the canonical/English name and every translation, not
  // just the localized label shown, so typing any of them finds the skill.
  const searchTermsByValue = useMemo(
    () =>
      new Map(
        allSkills.map((skill) => [
          skill.id,
          [skill.baseName, ...Object.values(skill.translations)]
            .filter(Boolean)
            .join(' ')
            .toLowerCase(),
        ]),
      ),
    [allSkills],
  );

  const renderOption = useCallback(
    ({ option, isInsideSelect }: RenderOptionParams<OptionBase>) => {
      if (isInsideSelect) {
        return <OptionLabel option={option} isInsideSelect={isInsideSelect} />;
      }
      return <SkillOptionLabel label={option.label} />;
    },
    [],
  );

  const filterOption = useCallback(
    (option: { data: OptionBase }, rawInput: string) => {
      const input = rawInput.trim().toLowerCase();
      if (!input) {
        return true;
      }
      return (searchTermsByValue.get(option.data.value) ?? '').includes(input);
    },
    [searchTermsByValue],
  );

  const options = useMemo(() => allSkills.map(mapOption), [allSkills]);
  return (
    <Container>
      <StyledSelectSingle
        {...props}
        options={options}
        renderOption={renderOption}
        filterOption={filterOption}
        heightMode="normal"
        borderless={false}
        placeholder={placeholder ?? t('skillsPlaceholder')}
      />
    </Container>
  );
};

const mapOption = (skill: Skill): OptionBase => ({
  value: skill.id,
  label: skill.name,
});

const StyledSelectSingle = styled(SelectSingle)({
  width: '100%',
});

const Container = styled.div({
  display: 'flex',
  alignItems: 'center',
  minHeight: '2.75rem',
});
